import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate, useParams } from 'react-router-dom'
import { workflowStore } from '@/stores/WorkflowStore'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import {
  ArrowLeftIcon,
  PlayIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { formatDistanceToNow } from 'date-fns'
import { WorkflowStatus } from '@/types'

const WorkflowExecutionPage = observer(() => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [isExecuting, setIsExecuting] = useState(false)
  const [inputMode, setInputMode] = useState<'form' | 'json'>('form')
  const [formInputs, setFormInputs] = useState<Record<string, string>>({})
  const [jsonInput, setJsonInput] = useState('')
  const [executionResult, setExecutionResult] = useState<any>(null)
  const [executionError, setExecutionError] = useState<string | null>(null)

  useEffect(() => {
    const loadWorkflow = async () => {
      if (!id) {
        navigate('/workflows')
        return
      }

      setIsLoading(true)
      try {
        const workflow = await workflowStore.fetchWorkflowById(id)
        if (!workflow) {
          toast.error('Workflow not found')
          navigate('/workflows')
          return
        }

        if (workflow.status !== WorkflowStatus.ACTIVE) {
          toast.error('Only active workflows can be executed')
          navigate('/workflows')
          return
        }

        workflowStore.loadWorkflow(workflow)

        // Initialize form inputs based on workflow nodes
        const inputs: Record<string, string> = {}
        const startNodes = workflow.definition?.nodes?.filter((n: any) => n.type === 'start') || []
        if (startNodes.length > 0) {
          inputs['input'] = ''
        }
        setFormInputs(inputs)
        setJsonInput(JSON.stringify({ input: '' }, null, 2))
      } catch (error) {
        toast.error('Failed to load workflow')
        navigate('/workflows')
      } finally {
        setIsLoading(false)
      }
    }

    loadWorkflow()
  }, [id, navigate])

  const handleExecute = async () => {
    if (!id) return

    setIsExecuting(true)
    setExecutionResult(null)
    setExecutionError(null)

    try {
      let input: Record<string, any>
      if (inputMode === 'json') {
        try {
          input = JSON.parse(jsonInput)
        } catch (e) {
          throw new Error('Invalid JSON input')
        }
      } else {
        input = formInputs
      }

      const result = await workflowStore.executeWorkflow(id, input)
      setExecutionResult(result)
      toast.success('Workflow executed successfully!')
    } catch (error: any) {
      setExecutionError(error.message || 'Workflow execution failed')
      toast.error('Workflow execution failed')
    } finally {
      setIsExecuting(false)
    }
  }

  const handleFormInputChange = (key: string, value: string) => {
    setFormInputs((prev) => ({ ...prev, [key]: value }))
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5 text-green-600" />
      case 'failed':
        return <XCircleIcon className="w-5 h-5 text-red-600" />
      case 'running':
        return <ClockIcon className="w-5 h-5 text-blue-600 animate-spin" />
      default:
        return <ClockIcon className="w-5 h-5 text-muted-foreground" />
    }
  }

  const getStatusBadgeVariant = (status: string) => {
    const variants: Record<string, 'primary' | 'success' | 'warning' | 'gray'> = {
      running: 'primary',
      completed: 'success',
      failed: 'warning',
      pending: 'gray',
    }
    return variants[status] || 'gray'
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading workflow...</p>
        </div>
      </div>
    )
  }

  const workflow = workflowStore.selectedWorkflow
  if (!workflow) return null

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/workflows')}>
            <ArrowLeftIcon className="w-4 h-4 mr-1" />
            Back to Workflows
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{workflow.name}</h1>
            {workflow.description && (
              <p className="text-muted-foreground mt-1">{workflow.description}</p>
            )}
          </div>
        </div>
      </div>

      {/* Workflow Info */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Status:</span>
              <Badge variant="success">{workflow.status}</Badge>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Nodes:</span>
              <span className="font-medium">{workflow.definition?.nodes?.length || 0}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Connections:</span>
              <span className="font-medium">{workflow.definition?.edges?.length || 0}</span>
            </div>
          </div>
          <Button
            variant="secondary"
            onClick={() => navigate(`/workflows/${id}`)}
          >
            View Workflow
          </Button>
        </div>
      </Card>

      {/* Input Section */}
      <Card>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">Execution Input</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setInputMode('form')}
                className={`px-3 py-1 text-sm rounded-lg ${
                  inputMode === 'form'
                    ? 'bg-primary-500/15 text-primary-700 font-medium'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                Form
              </button>
              <button
                onClick={() => setInputMode('json')}
                className={`px-3 py-1 text-sm rounded-lg ${
                  inputMode === 'json'
                    ? 'bg-primary-500/15 text-primary-700 font-medium'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                JSON
              </button>
            </div>
          </div>

          {inputMode === 'form' ? (
            <div className="space-y-4">
              {Object.keys(formInputs).map((key) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-foreground mb-1 capitalize">
                    {key}
                  </label>
                  <textarea
                    value={formInputs[key]}
                    onChange={(e) => handleFormInputChange(key, e.target.value)}
                    placeholder={`Enter ${key}...`}
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div>
              <textarea
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                placeholder='{"input": "Your input here..."}'
                rows={8}
                className="w-full px-3 py-2 border border-border rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          )}

          <Button
            variant="primary"
            onClick={handleExecute}
            disabled={isExecuting}
            isLoading={isExecuting}
            className="w-full"
          >
            <PlayIcon className="w-4 h-4 mr-2" />
            {isExecuting ? 'Executing...' : 'Execute Workflow'}
          </Button>
        </div>
      </Card>

      {/* Execution Result */}
      {(executionResult || executionError) && (
        <Card>
          <h2 className="text-lg font-semibold text-foreground mb-4">Execution Result</h2>

          {executionError ? (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-600 mb-1">Error</h3>
                  <p className="text-sm text-red-600">{executionError}</p>
                </div>
              </div>
            </div>
          ) : executionResult ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                {getStatusIcon(executionResult.status)}
                <Badge variant={getStatusBadgeVariant(executionResult.status)}>
                  {executionResult.status}
                </Badge>
                {executionResult.completed_at && (
                  <span className="text-sm text-muted-foreground">
                    {formatDistanceToNow(new Date(executionResult.completed_at), {
                      addSuffix: true,
                    })}
                  </span>
                )}
              </div>

              {executionResult.result && (
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">Output</h3>
                  <pre className="bg-muted/50 border border-border rounded-lg p-4 text-sm overflow-x-auto">
                    {typeof executionResult.result === 'string'
                      ? executionResult.result
                      : JSON.stringify(executionResult.result, null, 2)}
                  </pre>
                </div>
              )}

              {executionResult.error && (
                <div>
                  <h3 className="text-sm font-semibold text-red-600 mb-2">Error Details</h3>
                  <pre className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-sm overflow-x-auto text-red-600">
                    {executionResult.error}
                  </pre>
                </div>
              )}

              {executionResult.execution_time && (
                <div className="text-sm text-muted-foreground">
                  <span className="font-medium">Execution time:</span>{' '}
                  {executionResult.execution_time}s
                </div>
              )}
            </div>
          ) : null}
        </Card>
      )}

      {/* Info Box */}
      <Card className="bg-blue-500/10 border-blue-500/20">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <svg
              className="w-5 h-5 text-blue-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-blue-600 mb-1">About Workflow Execution</h3>
            <p className="text-sm text-blue-600">
              Workflows are executed asynchronously. You can provide input in either form or JSON
              format. The workflow will process through all nodes and return the final result.
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
})

export default WorkflowExecutionPage
